import React, {useCallback, useEffect, useRef} from "react";
import {Typography, Box, CircularProgress} from "@mui/material";
import {fetchMessages} from "@store/chatSlice.tsx";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router";
import {AppDispatch} from "@store/store.tsx";
import ChatMessages from "./chatMessages";
import RaiseTicketOption from "./raiseTicketOption";

interface ConversationProps {
    setInitialLoader: React.Dispatch<React.SetStateAction<boolean>>;
    initialLoader: boolean;
}

const Conversation: React.FC<ConversationProps> = ({
                                                       setInitialLoader,
                                                       initialLoader,
                                                   }) => {
    const dispatch = useDispatch<AppDispatch>();
    const {id} = useParams();
    const prevScrollHeight = useRef(0);
    const topMessageRef = useRef<HTMLDivElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const {messages, users, loading, hasMore, page} = useSelector(
        (state: any) => state.chat.chat
    );

    const onIntersection = useCallback(
        ([entry]: any) => {
            if (entry.isIntersecting && id && !loading && hasMore) {
                prevScrollHeight.current = topMessageRef.current?.scrollHeight ?? 0;
                dispatch(fetchMessages({ticketId: Number(id), page: page}));
            }
            setInitialLoader(false);
        },
        [loading, initialLoader]
    );

    useEffect(() => {
        const currentRef = scrollRef.current;
        if (!currentRef) return;

        const observer = new IntersectionObserver(onIntersection, {
            root: topMessageRef.current,
            rootMargin: "0px",
            threshold: 0.1,
        });

        observer.observe(currentRef);

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) observer.unobserve(currentRef);
        };
    }, [onIntersection]);

    return (
        <Box
            ref={topMessageRef}
            sx={{
                overflowY: "auto",
                width: "100%",
                maxHeight: {
                    xs: "calc(100vh - 206px)",
                    sm: "calc(100vh - 222px)",
                },
                display: "flex",
                flexDirection: "column-reverse",
            }}
            className="hidden-scrollbar px-4 md:px-2 "
        >
            <ChatMessages
                scrollRef={scrollRef}
                comments={messages}
                users={Object.values(users)}
            />

            {loading && (
                <Box className="h-[5px] py-8 flex justify-center items-center">
                    <CircularProgress size="20px"/>
                </Box>
            )}

            {id === 'new' && (
                <>
                    <RaiseTicketOption/>
                    <Typography
                        className="flex justify-center lg:px-36 xl:px-48 md:px-12 pt-6 !text-xs text-center md:text-left">
            <span style={{display: "contents", color: "red"}}>
              Disclaimer:
            </span>{" "}
                        The sole purpose of the chat functionality is used for diagnosing
                        and troubleshooting OrganOx metra related issues. The disclosure of
                        protected health information (PHI) within the chat is strictly
                        prohibited
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default Conversation;
